import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import styles from "./Footer.module.css";
import {
  HorizontalPosition,
  TemplateElement,
  TempleteElementGroupItem,
} from "~/entities/baseEntities";
import { uploadcareConfig } from "~/configs/uploadcareConfig";
import {
  TemplateElementButtonHorizontalPosition,
  TemplateElementHorizontalPosition,
  TemplateElementInRowHorizontalPosition,
} from "~/theme/models/themeModels";
import { getSocialIcon } from "~/helpers/cssHelpers";
import Button from "~/components/Button/Button";
import { FooterType, OnlineTemplate } from "~/entities/templateEntities";

const renderGroupItem = (
  item: TempleteElementGroupItem,
  template: OnlineTemplate,
  index: number,
  footer: FooterType,
  branding: any,
  isInsideRow: boolean = false
) => {
  const horizontalPosition = item.position || HorizontalPosition.Left;

  const leftIconValue = item.leftIcon
    ? (getSocialIcon(item.leftIcon) as IconProp)
    : null;
  const rightIconValue = item.rightIcon
    ? (getSocialIcon(item.rightIcon) as IconProp)
    : null;
  const leftIcon = leftIconValue ? (
    <FontAwesomeIcon icon={leftIconValue} className={styles.icon} size="xl" />
  ) : null;
  const rightIcon = rightIconValue ? (
    <FontAwesomeIcon icon={rightIconValue} className={styles.icon} size="xl" />
  ) : null;

  if (item.type === TemplateElement.Link) {
    return (
      <a
        className={styles.link}
        href={item.url || undefined}
        key={index}
        style={{
          color: footer.primaryFontColour,
          [isInsideRow ? "justifySelf" : "alignSelf"]: isInsideRow
            ? TemplateElementInRowHorizontalPosition[horizontalPosition]
            : TemplateElementHorizontalPosition[horizontalPosition],
        }}
      >
        {leftIcon}
        <span>{item.label}</span>
        {rightIcon}
      </a>
    );
  }
  if (item.type === TemplateElement.Button) {
    return (
      <a
        href={item.url || undefined}
        key={index}
        style={{
          textDecoration: "none",
          width: "100%",
          display: "flex",
          justifyContent:
            TemplateElementButtonHorizontalPosition[horizontalPosition],
        }}
      >
        <Button className={styles.button}>
          {leftIcon}
          <span>{item.label}</span>
          {rightIcon}
        </Button>
      </a>
    );
  }
  if (item.type === TemplateElement.StyledLink) {
    return (
      <a
        className={styles.styledLink}
        href={item.url || undefined}
        key={index}
        style={{
          color: footer.secondaryFontColour,
          [isInsideRow ? "justifySelf" : "alignSelf"]: isInsideRow
            ? TemplateElementInRowHorizontalPosition[horizontalPosition]
            : TemplateElementHorizontalPosition[horizontalPosition],
        }}
      >
        {leftIcon}
        <span>{item.label}</span>
        {rightIcon}
      </a>
    );
  }
  if (item.type === TemplateElement.Socials && branding.socials?.length > 0) {
    return (
      <div
        className={styles.socials}
        style={{
          [isInsideRow ? "justifySelf" : "alignSelf"]: isInsideRow
            ? TemplateElementInRowHorizontalPosition[horizontalPosition]
            : TemplateElementHorizontalPosition[horizontalPosition],
        }}
        key={index}
      >
        {branding.socials.map((social: any, i: number) => (
          <a href={social.url} target="_blank" rel="noreferrer" key={i}>
            {social.customIcon ? (
              <img
                src={uploadcareConfig.getUrl(
                  social.customIcon.fileId,
                  social.customIcon.modifiers
                )}
                alt={social.customIcon.fileName}
              />
            ) : (
              <FontAwesomeIcon
                icon={getSocialIcon(social.icon) as IconProp}
                color={footer.secondaryFontColour}
                className={styles.socialIcon}
                key={index}
              />
            )}
          </a>
        ))}
      </div>
    );
  }
  if (item.type === TemplateElement.Text) {
    return (
      <div
        className={styles.text}
        key={index}
        style={{
          color: footer.primaryFontColour,
          [isInsideRow ? "justifySelf" : "alignSelf"]: isInsideRow
            ? TemplateElementInRowHorizontalPosition[horizontalPosition]
            : TemplateElementHorizontalPosition[horizontalPosition],
        }}
      >
        {item.label}
      </div>
    );
  }
  if (item.type === TemplateElement.Row) {
    return (
      <div className={styles.row} key={index}>
        {item.children?.map((child: any, i: number) =>
          renderGroupItem(child, template, i, footer, branding, true)
        )}
      </div>
    );
  }
  if (item.type === TemplateElement.Copyright) {
    return (
      <div
        className={styles.copyright}
        style={{
          color: footer.primaryFontColour,
          [isInsideRow ? "justifySelf" : "alignSelf"]: isInsideRow
            ? TemplateElementInRowHorizontalPosition[horizontalPosition]
            : TemplateElementHorizontalPosition[horizontalPosition],
        }}
        key={index}
      >
        {branding.copyrightMessage ||
          `© ${new Date().getFullYear()} Dnero. All rights reserved.`}
      </div>
    );
  }
  if (item.type === TemplateElement.Image && item.image) {
    return (
      <img
        className={styles.image}
        src={uploadcareConfig.getUrl(item.image.fileId, item.image.modifiers)}
        alt={item.image.fileName}
        key={index}
        style={{
          [isInsideRow ? "justifySelf" : "alignSelf"]: isInsideRow
            ? TemplateElementInRowHorizontalPosition[horizontalPosition]
            : TemplateElementHorizontalPosition[horizontalPosition],
        }}
      />
    );
  }
  return <></>;
};

const Footer: React.FC<{ template: OnlineTemplate }> = ({ template }) => {
  const { footer, branding } = template;
  const footerBackgroundColor = template.footer.backgroundColour;

  return (
    <footer
      className={styles.wrapper}
      style={{ backgroundColor: footerBackgroundColor }}
    >
      <div className={styles.groupsWrapper}>
        {footer.groups.map((group, idx) => (
          <div className={styles.group} key={idx}>
            {group.title && (
              <div
                className={styles.groupTitle}
                style={{
                  color: footer.primaryFontColour,
                  alignSelf:
                    TemplateElementHorizontalPosition[
                      group.titlePosition || HorizontalPosition.Left
                    ],
                }}
              >
                {group.title}
              </div>
            )}
            <div className={styles.groupItems}>
              {group.items.map((item, i) =>
                renderGroupItem(item, template, i, footer, branding)
              )}
            </div>
          </div>
        ))}
      </div>
    </footer>
  );
};

export default Footer;
